/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CostDrilldownModal.tsx
 *     Feature         :   258-cost-analytics-azure-spend (Cost-Analytics fine-tuning #4/#5/#6)
 *     Description     :   One-level-down breakdown for a clicked row on any Cost-Analytics
 *                         list (subscription / service / resource / RG / storage account /
 *                         cost dimension). Fetches /api/cost-insights/azure/drilldown scoped
 *                         to the same window + subscription filter, showing the entity's
 *                         daily trend + primary (and optional secondary) sub-breakdown.
 *                         EVERY list row also carries a hover popup that breaks that item
 *                         down one level further (/subdrill), within the modal's scope.
 *                         Read-only; kit + tokens (Principle XLV).
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-27
 * =========================================================================================
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';
import { apiClient } from '../../services/apiClient';
import { Modal, LoadingState, ErrorState, EmptyState, chartTooltipStyle } from '../../components/ui';

export type CostDrilldownDim =
  | 'subscription' | 'service' | 'resource' | 'resourceGroup' | 'storageAccount' | 'costDimension';

export interface DrilldownTarget {
  dimension: CostDrilldownDim;
  key: string;       // the clicked row's id/value
  label: string;     // human label for the header
}

interface DrilldownRow { label: string; key: string | null; sublabel: string | null; usd: number }
interface DrilldownList { label: string; dim: CostDrilldownDim; rows: DrilldownRow[] }
interface DrilldownPayload {
  dimension: CostDrilldownDim;
  key: string;
  title: string;
  total: number;
  primary: DrilldownList;
  secondary: DrilldownList | null;
  byDay: Array<{ usageDate: string; usd: number }>;
}
interface SubdrillPayload {
  total: number;
  childLabel: string;
  rows: Array<{ label: string; usd: number }>;
}
type Win = { from: string; to: string; subscriptionId: string | null };

const usd = (v: number | null | undefined): string =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// ── hover popup: break a line item down one level further, within the modal's scope ──────
const HoverBreakdown: React.FC<{
  scope: { dimension: CostDrilldownDim; key: string };
  win: Win;
  lineDim: CostDrilldownDim;
  lineKey: string | null;
  children: React.ReactNode;
}> = ({ scope, win, lineDim, lineKey, children }) => {
  const [open, setOpen] = React.useState(false);
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const ref = React.useRef<HTMLDivElement>(null);

  const q = useQuery<SubdrillPayload>({
    enabled: open && !!lineKey,
    queryKey: ['cost-subdrill', scope.dimension, scope.key, lineDim, lineKey, win.from, win.to, win.subscriptionId],
    queryFn: async () => {
      const p = new URLSearchParams({
        scopeDim: scope.dimension, scopeKey: scope.key, lineDim, lineKey: lineKey ?? '',
        from: win.from, to: win.to,
      });
      if (win.subscriptionId) p.set('subscriptionId', win.subscriptionId);
      const res = await apiClient.get<{ success: boolean; data: SubdrillPayload }>(
        `/api/cost-insights/azure/subdrill?${p.toString()}`,
      );
      return res.data.data;
    },
    staleTime: 60_000,
  });

  if (!lineKey) return <>{children}</>;

  const onEnter = () => {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    setOpen(true);
  };
  const flipUp = !!rect && rect.bottom > window.innerHeight - 240;
  const style: React.CSSProperties = rect
    ? flipUp
      ? { position: 'fixed', left: rect.left, bottom: window.innerHeight - rect.top + 4, width: 288, zIndex: 60 }
      : { position: 'fixed', left: rect.left, top: rect.bottom + 4, width: 288, zIndex: 60 }
    : {};

  return (
    <div ref={ref} onMouseEnter={onEnter} onMouseLeave={() => setOpen(false)}>
      {children}
      {open && rect && createPortal(
        <div style={style}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 shadow-lg">
          {q.isLoading || !q.data ? (
            <p className="text-xs text-[var(--color-text-tertiary)]">Loading…</p>
          ) : q.data.rows.length === 0 ? (
            <p className="text-xs text-[var(--color-text-tertiary)]">No further breakdown.</p>
          ) : (
            <>
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]">{q.data.childLabel}</p>
              <ul className="space-y-0.5">
                {q.data.rows.map((r, i) => {
                  // Spec 298 FR-004: a line whose spend nets to $0.00 has no shares; its rows show "—", not "0%"
                  const pct = q.data!.total > 0 ? Math.round((r.usd / q.data!.total) * 100) : null;
                  return (
                    <li key={`${r.label}-${i}`} className="flex items-center justify-between gap-2 text-xs">
                      <span className="min-w-0 flex-1 truncate text-[var(--color-text-secondary)]" title={r.label}>{r.label}</span>
                      <span className="shrink-0 tabular-nums text-[var(--color-text-tertiary)]"
                        title={pct === null ? 'This line nets to $0.00, so no share' : undefined}>{pct === null ? '—' : `${pct}%`}</span>
                      <span className="shrink-0 tabular-nums text-[var(--color-text-primary)]">{usd(r.usd)}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

const SubBreakdown: React.FC<{
  list: DrilldownList;
  total: number;
  scope: { dimension: CostDrilldownDim; key: string };
  win: Win;
}> = ({ list, total, scope, win }) => (
  <div>
    <p className="mb-2 text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">{list.label}</p>
    {list.rows.length === 0 ? (
      <p className="text-sm text-[var(--color-text-secondary)]">No further breakdown.</p>
    ) : (
      <ul className="divide-y divide-[var(--color-border)]">
        {list.rows.map((r, i) => {
          const pct = total > 0 ? Math.round((r.usd / total) * 100) : 0;
          return (
            <li key={`${r.label}-${i}`}>
              <HoverBreakdown scope={scope} win={win} lineDim={list.dim} lineKey={r.key}>
                <div className={`flex items-center justify-between gap-3 py-1.5 text-sm ${r.key ? 'cursor-help hover:bg-[var(--color-bg-subtle)]' : ''}`}>
                  <span className="min-w-0 flex-1 truncate text-[var(--color-text-primary)]" title={r.label}>
                    {r.label}
                    {r.sublabel ? <span className="text-[var(--color-text-tertiary)]"> · {r.sublabel}</span> : null}
                  </span>
                  <span className="shrink-0 tabular-nums text-[var(--color-text-secondary)]">{pct}%</span>
                  <span className="shrink-0 tabular-nums font-medium text-[var(--color-text-primary)]">{usd(r.usd)}</span>
                </div>
              </HoverBreakdown>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

export const CostDrilldownModal: React.FC<{
  target: DrilldownTarget | null;
  window: Win;
  onClose: () => void;
}> = ({ target, window: win, onClose }) => {
  const q = useQuery<DrilldownPayload>({
    enabled: !!target,
    queryKey: ['cost-drilldown', target?.dimension, target?.key, win.from, win.to, win.subscriptionId],
    queryFn: async () => {
      const p = new URLSearchParams({ dimension: target!.dimension, key: target!.key, from: win.from, to: win.to });
      if (win.subscriptionId) p.set('subscriptionId', win.subscriptionId);
      const res = await apiClient.get<{ success: boolean; data: DrilldownPayload }>(
        `/api/cost-insights/azure/drilldown?${p.toString()}`,
      );
      return res.data.data;
    },
    staleTime: 60_000,
  });

  const d = q.data;
  const scope = target ? { dimension: target.dimension, key: target.key } : { dimension: 'subscription' as CostDrilldownDim, key: '' };
  return (
    <Modal
      isOpen={!!target}
      onClose={onClose}
      size="lg"
      title={target ? <span>{q.data?.title ?? target.label} · <span className="text-[var(--color-text-secondary)]">{target.label}</span></span> : ''}
    >
      {!target ? null : q.error ? (
        <ErrorState title="Failed to load breakdown" message={(q.error as Error).message} onRetry={() => q.refetch()} />
      ) : q.isLoading || !d ? (
        <LoadingState rows={5} />
      ) : d.total <= 0 ? (
        <EmptyState title="No cost for this item" message="No billing rows for this entity in the selected window." />
      ) : (
        <div className="space-y-5">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-[var(--color-text-secondary)]">{win.from} → {win.to}</span>
            <span className="text-xl font-semibold text-[var(--color-text-primary)]">{usd(d.total)}</span>
          </div>

          <div>
            <p className="mb-1 text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">Spend per day</p>
            <div className="h-40 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.byDay} margin={{ top: 4, right: 8, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="usageDate" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                  <Tooltip {...chartTooltipStyle} formatter={(v) => [usd(Number(v)), 'Spend']} />
                  <Bar dataKey="usd" fill="#4f8ff7" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <p className="text-xs text-[var(--color-text-tertiary)]">Hover any line below to break it down one level further.</p>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <SubBreakdown list={d.primary} total={d.total} scope={scope} win={win} />
            {d.secondary ? <SubBreakdown list={d.secondary} total={d.total} scope={scope} win={win} /> : null}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CostDrilldownModal;
